import { PhaseActionBar } from "../../../features/work-session";
import type { PrimaryWorkAction } from "../../../features/work-session";
import { TaskSecondaryActions } from "./TaskSecondaryActions";

type Props = {
  /** From getPrimaryWorkAction — null when nothing to tap (still may render alongside "Planen"). */
  action: PrimaryWorkAction | null;
  canPlan: boolean;
  isTimeScheduled: boolean;
  /** ISO anchor of the running phase — feeds the elapsed chip. */
  elapsedSince: string | null;
  isPending: boolean;
  failed: boolean;
  confirmedAt: string | null;
  onAction: (action: PrimaryWorkAction) => void;
  onUndo?: () => void;
  onPlan: () => void;
};

/**
 * The mobile bottom bar — the task-page counterpart of `AppointmentActions`. Same floating,
 * capped-width card, fully rounded, with real air below it — see that component's comment for the
 * full rationale (`position: fixed` vs `absolute`, the commit `7d75d2d` regression, the safe-area
 * margin). `WorkerTaskPage` reserves matching space itself (`pb-32` on its mobile-only content
 * wrapper) since this is a fixed overlay, not a flex sibling.
 *
 * Unlike the appointment bar, the primary phase button and "Planen"/"Umplanen" are not mutually
 * exclusive here — an unscheduled task has no `action` at all and still needs "Planen" — so both
 * are rendered independently instead of assuming exactly one side is ever present.
 */
export const TaskActions = ({
  action,
  canPlan,
  isTimeScheduled,
  elapsedSince,
  isPending,
  failed,
  confirmedAt,
  onAction,
  onUndo,
  onPlan,
}: Props) => (
  // `fixed` escapes `.page`'s own horizontal padding entirely, so it is restated here (`px-4`,
  // matching `.page`'s padding at this breakpoint) rather than relied on.
  <div className="fixed inset-x-0 bottom-0 z-30 mx-auto max-w-md px-4">
    <div
      className="rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_4px_20px_rgba(15,23,42,0.12)]"
      style={{ marginBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="flex items-stretch gap-2">
        {action && (
          <PhaseActionBar
            action={action}
            className="min-w-0 flex-1"
            elapsedSince={elapsedSince}
            isPending={isPending}
            failed={failed}
            confirmedAt={confirmedAt}
            onAction={onAction}
            onUndo={onUndo}
            compact
          />
        )}

        <TaskSecondaryActions
          appearance="bar"
          canPlan={canPlan}
          isTimeScheduled={isTimeScheduled}
          onPlan={onPlan}
        />
      </div>
    </div>
  </div>
);
